import { THEME, ThemeProvider } from '@vodacomsa/ns-react-components';
import {
  Form,
  Formik
} from 'formik';
import { FormBuilder } from '../../components/FormBuilder';
import { formFields } from './FundingApplicationDetailsConstants';
import { StatusCodes } from 'http-status-codes';
import React, { useMemo, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { AccountsRoutes, ApplicationRoutes, StaticRoutes } from '../../Router';
import { postFundingDetails, updateFundingDetails } from '../../api/vodaLend';
import {
  Header,
  SupportPopup
} from '../../components';
import { useLoader } from '../../components/Loader/useLoader';
import {
  AnnualBusinessIncome,
  FormContainer
} from '../../constants';
import {
  convertCurrencyToAmount,
  convertDateSelectorToDateValueFormat,
  convertDateValueToDateSelectorFormat,
  mapValues,
  replaceUndefined,
  stripEmpty
} from '../../helpers';
import { findFirstValidationError } from '../../helpers/formik';
import { isValidDate } from '../../helpers/strings';
import {
  fundingDetailsState,
  fundingDetailsSubmit,
  applicationsList
} from '../../recoil';
import { useTealium } from '../../tealium/useTealium';
import { ValidationSchema } from './ValidationSchema';

export const FundingApplicationDetails = () => {
  const fetchedFundingDetails = useRecoilValue(fundingDetailsState);
  const [fundingDetails, setFundingDetails] = useRecoilState(fundingDetailsSubmit);
  const appsList = useRecoilValue(applicationsList);
  const [openLoader, closeLoader] = useLoader();
  const navigate = useNavigate();
  const location = useLocation();
  const { track, events } = useTealium();
  let selectedProductId = sessionStorage.getItem('productId');
  const setFundingDetailsResponse = useSetRecoilState(fundingDetailsState);

  useEffect(() => {
    track(events.fundinApplicationDetails.pageView());
  }, []);

  useEffect(() => {
    localStorage.setItem('loanApplicationStepKey', '');
  }, []);

  const DetailsEntry = () => {
    const formRef = useRef();
    const formStartedRef = useRef(false);
    const formDetails = useMemo(() => replaceUndefined({
      ...fundingDetails,
      loanAmount: fundingDetails?.loanAmount ? fundingDetails?.loanAmount : '',
      financeReason: fundingDetails?.financeReason,
      financeTenure: fundingDetails?.financeTenure,
      timeInBusiness: isValidDate(convertDateValueToDateSelectorFormat(fundingDetails.timeInBusiness)) ? convertDateValueToDateSelectorFormat(fundingDetails.timeInBusiness) : null,
      businessType: fundingDetails?.businessType,
      revenue: AnnualBusinessIncome.find((i) => convertCurrencyToAmount(i.value) === fundingDetails?.revenue)?.value
    }), [fundingDetails]);

    const handleFormFocus = () => {
      if (formStartedRef.current) {
        return;
      }
      formStartedRef.current = true;
      track(events.fundinApplicationDetails.formStart());
    };

    const formatFormData = (formValues) => {
      let formattedFormData;
      const loanAmount = formValues?.loanAmount;
      const businessType = formValues?.businessType;
      const financeReason = formValues?.financeReason;
      const financeTenure = formValues?.financeTenure;
      const revenue = convertCurrencyToAmount(formValues?.revenue);
      const timeInBusiness = convertDateSelectorToDateValueFormat(formValues?.timeInBusiness);
      if (selectedProductId) {
        const productId = selectedProductId;
        formattedFormData = { ...formValues, loanAmount, financeReason, businessType, financeTenure, revenue, timeInBusiness, productId };
      } else {
        formattedFormData = { ...formValues, loanAmount, financeReason, businessType, financeTenure, revenue, timeInBusiness };
      }
      return mapValues(stripEmpty(formattedFormData));
    };

    const CheckPostRequestType = (fundingDetail) => {
      let checkPostRequestType;
      if (fundingDetail.error) {
        checkPostRequestType = true;
      } else {
        checkPostRequestType = Object.values(fundingDetail).every(x => x === null || x === '');
      }
      return checkPostRequestType;
    };

    const handleSubmit = async (formValues) => {

      let fundingDetailResponse;
      openLoader();

      const formattedFormData = formatFormData(formValues);
      setFundingDetails(formattedFormData);

      try {
        track(events.fundinApplicationDetails.formEnd(formattedFormData?.loanAmount, formattedFormData?.financeTenure, formattedFormData?.financeReason));
      } catch (e) {
        return;
      }

      const isPreSubmissionJourney = !Array.isArray(appsList) || appsList.length === 0;
      const isPostRequest = CheckPostRequestType(fetchedFundingDetails);

      if (isPostRequest) {
        fundingDetailResponse = await postFundingDetails(formattedFormData);
      } else {
        fundingDetailResponse = await updateFundingDetails(formattedFormData);
      }

      if (fundingDetailResponse.status === StatusCodes.CREATED || fundingDetailResponse.status === StatusCodes.OK) {
        sessionStorage.removeItem('productId');
        closeLoader();
        setFundingDetailsResponse(fundingDetailResponse?.data);
        if (isPreSubmissionJourney) {
          navigate(StaticRoutes.Products);
        } else {
          navigate(ApplicationRoutes.personalDetails);
        }
      }

    };

    const handleFormikTouchedErrors = (touched, errors) => {
      const errorMessage = findFirstValidationError(errors, touched);
      if (!errorMessage) {
        return;
      }
      track(events.error('form', location.pathname, errorMessage));
    };

    const backClick = () => {
      if (!Array.isArray(appsList) || appsList.length === 0) {
        navigate(-1);
      } else {
        navigate(AccountsRoutes.dashboard);
      }
    };

    const getLeftButtonTitle = () => (!Array.isArray(appsList) || appsList.length === 0) ? '' : 'Back';

    return (
      <div>
        <ThemeProvider theme={THEME.revamp} >
          <Formik
            initialValues={formDetails}
            onSubmit={handleSubmit}
            validate={(values) => {
              try {
                ValidationSchema.validateSync(values, { abortEarly: false });
                return {};
              } catch (error) {
                return error.inner.reduce((acc, err) => {
                  acc[err.path] = err.message;
                  return acc;
                }, {});
              }
            }}
            validateOnBlur={true}
            validateOnMount={true}
            innerRef={formRef}
          >
            {({ values, errors, touched }) => {
              handleFormikTouchedErrors(touched, errors);
              return (<div>
                <Form style={FormContainer} onFocus={handleFormFocus}>
                  <FormBuilder
                    fields={formFields}
                    title="Continue funding application"
                    subtitle="Please complete all fields below"
                    values={values}
                    errors={errors}
                    backClick={backClick}
                    nextClick={() => handleSubmit(values)}
                    leftButtonTitle={getLeftButtonTitle()}
                    touched={touched}
                  />
                </Form>
              </div>);
            }}
          </Formik>
        </ThemeProvider>
        <SupportPopup />
      </div>
    );
  };

  return (
    <div >
      <Header />
      <DetailsEntry />
    </div>
  );
};